import { useEffect, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, Snackbar, SnackbarCloseReason, Switch, Typography } from '@mui/material';
import * as yup from 'yup';
import { VTextField, VFieldMoneyNumber2, VFieldMoneyNumber4dn, VForm, useVForm, IVFormErrors, AutoCompleteProduto } from '../../../shared/forms';
import { IOrdemServicoItem, OrdemServicoProdutosService } from '../../../shared/services/api/servicos/OrdemServicoProdutosService';

interface IFormData {
  idprodutos?: string;
  descricaomanual?: string;
  quantidade: number;
  valorunitario: number;
  desconto?: number;
}

const formValidationSchema: yup.ObjectSchema<Pick<IFormData, 'quantidade' | 'valorunitario'>> = yup.object().shape({
  quantidade: yup.number().required('Quantidade é obrigatória').moreThan(0, 'Quantidade deve ser maior que zero'),
  valorunitario: yup.number().required('Valor é obrigatório').min(0),
});

interface IDialogItemOSProps {
  open: boolean;
  idos: string;
  onClose: () => void;
  aoSalvar: (item: IOrdemServicoItem) => void;
}

// Adicionar peça/produto na OS — busca no estoque via AutoCompleteProduto
// (mesmo mecanismo já usado em outras telas, sem a complexidade de
// margem/preço mínimo do DialogVenda, que não se aplica aqui) ou, com o
// switch ligado, item avulso (descrição livre, sem vínculo com o estoque).
// Cada item já sai salvo direto no backend (mesmo padrão de
// DialogCadastrarBem) — a dialog fica aberta pra adicionar o próximo.
export const DialogItemOS: React.FC<IDialogItemOSProps> = ({ open, idos, onClose, aoSalvar }) => {
  const { formRef, save } = useVForm();
  const [isLoading, setIsLoading] = useState(false);
  const [avulso, setAvulso] = useState(false);

  const [descricaoProduto, setDescricaoProduto] = useState('');
  const [unProduto, setUnProduto] = useState('');
  const [referenciaProduto, setReferenciaProduto] = useState('');

  const [quantidade, setQuantidade] = useState(0);
  const [valorUnitario, setValorUnitario] = useState(0);
  const [desconto, setDesconto] = useState(0);

  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error'>('warning');

  const total = Math.max(0, quantidade * valorUnitario - desconto);

  useEffect(() => {
    if (!open) return;
    setAvulso(false);
    setDescricaoProduto('');
    setUnProduto('');
    setReferenciaProduto('');
    setQuantidade(0);
    setValorUnitario(0);
    setDesconto(0);
    formRef.current?.setData({ idprodutos: '', descricaomanual: '', quantidade: '', valorunitario: '', desconto: '' });
  }, [open]);

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') return;
    setOpenmsg(false);
  };

  const handleSave = (dados: IFormData) => {
    formValidationSchema
      .validate(dados, { abortEarly: false })
      .then(() => {
        const descricao = avulso ? (dados.descricaomanual || '').trim() : descricaoProduto;
        if (!descricao) {
          setTms('error');
          setMsg(avulso ? 'Informe a descrição do item.' : 'Selecione um produto.');
          setOpenmsg(true);
          return;
        }
        if (!avulso && !dados.idprodutos) {
          setTms('error');
          setMsg('Selecione um produto.');
          setOpenmsg(true);
          return;
        }

        setIsLoading(true);
        OrdemServicoProdutosService
          .create(idos, {
            idproduto: avulso ? undefined : dados.idprodutos,
            avulso,
            descricao,
            referencia: avulso ? undefined : referenciaProduto,
            unidade: avulso ? undefined : unProduto,
            quantidade: dados.quantidade,
            valorunitario: dados.valorunitario,
            desconto: dados.desconto || 0,
          })
          .then((result) => {
            setIsLoading(false);
            if (result instanceof Error) {
              setTms('error');
              setMsg(result.message);
              setOpenmsg(true);
            } else {
              aoSalvar(result);
              setAvulso(false);
              setDescricaoProduto('');
              setUnProduto('');
              setReferenciaProduto('');
              setQuantidade(0);
              setValorUnitario(0);
              setDesconto(0);
              formRef.current?.setData({ idprodutos: '', descricaomanual: '', quantidade: '', valorunitario: '', desconto: '' });
            }
          });
      })
      .catch((errors: yup.ValidationError) => {
        const validationErrors: IVFormErrors = {};
        errors.inner.forEach(error => {
          if (!error.path) return;
          validationErrors[error.path] = error.message;
        });
        formRef.current?.setErrors(validationErrors);
      });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Adicionar Peça/Produto</DialogTitle>
      <DialogContent dividers>
        <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg} autoHideDuration={null} onClose={handleCloseMsg}>
          <Alert onClose={handleCloseMsg} severity={tms} variant="filled" sx={{ width: '100%' }}>
            {msg}
          </Alert>
        </Snackbar>
        <VForm ref={formRef} onSubmit={handleSave} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <Box>
            <Grid container spacing={2} padding={1}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={avulso} onChange={(e) => setAvulso(e.target.checked)} disabled={isLoading} />}
                  label="Item avulso (sem vínculo com o estoque)"
                />
              </Grid>

              {avulso ? (
                <Grid item xs={12}>
                  <VTextField size='small' fullWidth name='descricaomanual' disabled={isLoading} label='Descrição' placeholder='Ex.: Mão de obra terceirizada, peça reaproveitada...' />
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <AutoCompleteProduto
                    isExternalLoading={isLoading}
                    descricao={(valor) => setDescricaoProduto(valor || '')}
                    un={(valor) => setUnProduto(valor || '')}
                    referencia={(valor) => setReferenciaProduto(valor || '')}
                    pVenda={(valor) => {
                      if (valor !== undefined) {
                        setValorUnitario(valor);
                        formRef.current?.setFieldValue('valorunitario', valor);
                      }
                    }}
                  />
                </Grid>
              )}

              {!avulso && descricaoProduto && (
                <Grid item xs={12}>
                  <Alert severity='info' variant='outlined'>
                    {descricaoProduto}{unProduto ? ` — ${unProduto}` : ''}{referenciaProduto ? ` — Ref. ${referenciaProduto}` : ''}
                  </Alert>
                </Grid>
              )}

              <Grid item xs={4}>
                <VFieldMoneyNumber2
                  fullWidth
                  size='small'
                  name='quantidade'
                  label='Quantidade'
                  disabled={isLoading}
                  calcular={(valor) => setQuantidade(Number(valor) || 0)}
                />
              </Grid>
              <Grid item xs={4}>
                <VFieldMoneyNumber4dn
                  fullWidth
                  size='small'
                  name='valorunitario'
                  label='Valor Unitário'
                  disabled={isLoading}
                  calcular={(valor) => setValorUnitario(Number(valor) || 0)}
                />
              </Grid>
              <Grid item xs={4}>
                <VFieldMoneyNumber2
                  fullWidth
                  size='small'
                  name='desconto'
                  label='Desconto'
                  disabled={isLoading}
                  calcular={(valor) => setDesconto(Number(valor) || 0)}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant='subtitle2' align='right'>
                  Total do item: {total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </VForm>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>Fechar</Button>
        <Button variant='contained' onClick={save} disabled={isLoading}>Adicionar Item</Button>
      </DialogActions>
    </Dialog>
  );
};
